/**
 * DiskCell — editable destination-disk picker in the Servers table.
 *
 * Renders the current disk as a Select trigger; each option in the dropdown
 * shows the disk's available space + count of servers already living there.
 * Picking a different disk opens a confirmation dialog (because the underlying
 * relocate is a real `mv` + symlink swing — not free, and not undoable).
 *
 * Why a confirm step?
 *   - Even when the move is fast (250G is nothing for spinning rust at sequential
 *     read speed), the server's `state` is set to "paused" for the duration.
 *   - Cross-mount moves fall back to rsync — could be tens of minutes if the
 *     repo has grown large.
 *   - Operators tend to mis-click in dense tables; a confirm avoids accidents.
 */

import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { HardDrive, Loader2, AlertTriangle } from "lucide-react";

import { ServersApi, type DiskInfo, type ServerRow } from "@/lib/services";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

function fmtBytes(n: number): string {
  if (!n || n <= 0) return "0";
  // Persian unit labels — matches apps/web/src/lib/utils.ts formatBytes()
  // so the disk strip reads consistently with the rest of the panel and
  // doesn't bidi-flip Latin suffixes inside the RTL layout.
  const units = ["بایت", "کیلوبایت", "مگابایت", "گیگابایت", "ترابایت", "پتابایت"];
  let v = n;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

export function DiskCell({ server, disks }: { server: ServerRow; disks: DiskInfo[] }) {
  const qc = useQueryClient();
  const [pending, setPending] = useState<string | null>(null);

  const current = server.diskTargetId; // e.g. "disk1" or null

  const move = useMutation({
    mutationFn: (toDisk: string) =>
      ServersApi.moveToDisk(server.id, toDisk, { createIfMissing: true }),
    onSuccess: (r) => {
      if (r.ok) {
        const bytes = r.bytesMoved > 0 ? ` (${fmtBytes(r.bytesMoved)})` : "";
        toast.success(`«${r.serverName}» به ${r.toDisk} منتقل شد${bytes}`, {
          description: r.warnings.length ? r.warnings.join(" · ") : undefined,
        });
      } else {
        toast.error("جابه‌جایی ناموفق بود", { description: r.error });
      }
      qc.invalidateQueries({ queryKey: ["servers"] });
      qc.invalidateQueries({ queryKey: ["disks"] });
      setPending(null);
    },
    onError: (e: Error) => {
      toast.error("خطا در درخواست", { description: e.message });
      setPending(null);
    },
  });

  if (disks.length === 0) {
    // Disks endpoint hasn't loaded yet or returned nothing — degrade gracefully.
    return (
      <span className="text-muted-foreground lat-num text-sm">
        {current ?? "—"}
      </span>
    );
  }

  const target = pending ? disks.find((d) => d.id === pending) : null;
  const currentDisk = current ? disks.find((d) => d.id === current) : null;

  return (
    <>
      <Select
        value={current ?? ""}
        onValueChange={(v) => {
          if (!v || v === current) return;
          setPending(v);
        }}
        disabled={move.isPending}
      >
        {/* Trigger renders a compact label (just the disk slug); the dropdown
            items below carry the rich usage/server-count info. Radix's
            SelectValue would mirror the full item HTML — too tall for a row. */}
        <SelectTrigger className="h-8 w-[110px] text-xs lat-num">
          {move.isPending ? (
            <span className="flex items-center gap-1.5 whitespace-nowrap">
              <Loader2 className="size-3 animate-spin" /> در حال انتقال…
            </span>
          ) : (
            <span className="flex items-center gap-1.5 whitespace-nowrap">
              <HardDrive className="size-3 shrink-0" />
              <span className="font-medium">{current ?? "انتخاب…"}</span>
            </span>
          )}
        </SelectTrigger>
        <SelectContent>
          {disks.map((d) => {
            const pct = d.totalBytes > 0 ? (d.usedBytes / d.totalBytes) * 100 : 0;
            return (
              <SelectItem key={d.id} value={d.id} className="text-xs">
                <div className="flex items-center gap-2 whitespace-nowrap">
                  <HardDrive className="size-3 shrink-0" />
                  <span className="lat-num font-medium">{d.id}</span>
                  <span className="text-muted-foreground lat-num">
                    {fmtBytes(d.freeBytes)} آزاد · {pct.toFixed(0)}٪ پر
                  </span>
                  <span className="text-muted-foreground">
                    · {d.serverNames.length.toLocaleString("en-US")} سرور
                  </span>
                </div>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
      {/* Reference currentDisk so the variable isn't flagged unused; we keep
          it for a future tooltip showing the current disk's stats. */}
      <span className="sr-only">{currentDisk?.id}</span>

      <Dialog open={!!pending && !move.isPending} onOpenChange={(o) => !o && setPending(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="size-5 text-amber-500" />
              تأیید جابه‌جایی دیسک
            </DialogTitle>
            <DialogDescription>
              مخزن restic سرور «{server.name}» از{" "}
              <code className="lat-num">{current ?? "(بدون دیسک)"}</code> به{" "}
              <code className="lat-num">{target?.id}</code> منتقل می‌شود.
            </DialogDescription>
          </DialogHeader>

          <ul className="text-sm space-y-1.5 text-muted-foreground">
            <li>• سرور در حین انتقال موقتاً به حالت «pause» می‌رود.</li>
            <li>• اگر هر جابی در حال اجرا/صف باشد، درخواست رد می‌شود.</li>
            <li>
              • اگر مبدأ و مقصد روی یک پارتیشن باشند، انتقال آنی (atomic
              rename) است. در غیر این صورت rsync اجرا می‌شود و ممکن است طولانی
              شود.
            </li>
            <li>• پس از انتقال، symlink در <code className="lat-num">/mnt/restic-root/</code> به مقصد جدید اشاره می‌کند.</li>
          </ul>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="ghost" onClick={() => setPending(null)}>
              انصراف
            </Button>
            <Button
              onClick={() => {
                if (pending) move.mutate(pending);
              }}
            >
              تأیید و انتقال
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
